// The game page chart's arithmetic: its buckets, axis steps, points and
// the breaks in its line. Apart from chart-draw.ts, which draws it, so it runs (and
// is tested, ../chart.test.ts) without a browser.

import type { HistoryResponse, HourPoint, RangePoint, Resolution } from "../../shared/api.ts";

export const HOUR = 3600;
export const DAY = 86400;
const WEEK = 7 * DAY;

// Each resolution's bucket, as the API sizes it. Weeks start on Monday (UTC);
// the unix epoch was a Thursday, so Mondays fall 4 days after multiples of a
// week.
export const UNITS: Readonly<Record<Resolution, number>> = { hour: HOUR, day: DAY, week: WEEK };
const WEEK_OFFSET = 4 * DAY;

// The start of the bucket `t` (unix seconds) falls in.
export function bucketOf(t: number, resolution: Resolution): number {
  const unit = UNITS[resolution];
  const offset = resolution === "week" ? WEEK_OFFSET : 0;
  return Math.floor((t - offset) / unit) * unit + offset;
}

// A step for the y axis's gridlines near `raw`: 1, 2 or 5 times a power of
// ten.
export function niceStep(raw: number): number {
  const pow = 10 ** Math.floor(Math.log10(raw));
  const n = raw / pow;
  return (n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10) * pow;
}

// A point on the chart: hourly points carry player_count; daily and weekly
// points carry min/avg/max.
export type Point = HourPoint | RangePoint;

export const high = (p: Point): number => ("max" in p ? p.max : p.player_count);
export const low = (p: Point): number => ("min" in p ? p.min : p.player_count);
export const mid = (p: Point): number => ("avg" in p ? p.avg : p.player_count);

// Daily points are UTC days, so the last one is today: still filling, with
// min/avg/max over only the hours elapsed so far. Charted as an ordinary point
// it draws a cliff down to the overnight trough that reflates across the day,
// and it drags the range's high and low along with it. So leave it out — the
// hourly ranges already show today at a resolution where it means something.
// (The series is shared with the response cache, so copy rather than splice.)
// `now` is unix ms.
export function dropPartialDay(series: HistoryResponse, now = Date.now()): HistoryResponse {
  if (series.resolution !== "day" || !series.points.length) return series;

  const today = Math.floor(now / 1000 / DAY) * DAY;

  return series.points.at(-1)?.t === today
    ? { ...series, points: series.points.slice(0, -1) }
    : series;
}

// `points` split at missing buckets (more than `unit` apart), so gaps show as
// breaks in the line, not straight lines across them.
export function segmentsOf(points: readonly Point[], unit: number): Point[][] {
  const segments: Point[][] = [];
  for (const [i, p] of points.entries()) {
    const before = points[i - 1];
    if (!before || p.t - before.t > unit) segments.push([p]);
    else segments.at(-1)?.push(p);
  }
  return segments;
}

// The point of `points` (sorted by t) nearest to time `t`, the earlier on a
// tie; undefined for none. What the compare chart's cursor reads each line at.
export function nearestByTime<P extends { t: number }>(points: readonly P[], t: number): P | undefined {
  let lo = 0;
  let hi = points.length - 1;
  while (lo < hi) {
    const m = (lo + hi) >> 1;
    if ((points[m]?.t ?? 0) < t) lo = m + 1;
    else hi = m;
  }
  const after = points[lo];
  const before = points[lo - 1];
  return before && after && t - before.t <= after.t - t ? before : after;
}

// A log scale's gridlines from `min` to `max` (both clamped to at least 1).
// Its ends are 1, 2 or 5 times a power of ten, the nearest at or outside
// min and max, so the lines fill the chart; between them, every power of
// ten, and on a scale of two decades or less every 1, 2 and 5 too, so a
// short scale still has a few lines. The first and last are the ends.
export function logTicks(min: number, max: number): number[] {
  const nice = (e: number): number[] => [1, 2, 5].map((s) => s * 10 ** e);
  const below = (v: number): number => {
    const e = Math.floor(Math.log10(v));
    return [...nice(e)].reverse().find((n) => n <= v) ?? 10 ** e;
  };
  const above = (v: number): number => {
    const e = Math.floor(Math.log10(v));
    return [...nice(e), 10 ** (e + 1)].find((n) => n >= v) ?? 10 ** (e + 1);
  };

  const lo = below(Math.max(min, 1));
  let hi = above(Math.max(max, 1));
  if (hi <= lo) hi = above(lo * 1.5);

  const fine = Math.log10(hi / lo) <= 2;
  const ticks = [lo];
  for (let e = Math.floor(Math.log10(lo)); 10 ** e < hi; e++) {
    for (const n of fine ? nice(e) : [10 ** e]) {
      if (n > lo && n < hi) ticks.push(n);
    }
  }
  ticks.push(hi);
  return ticks;
}

// The charts' labels. Daily and weekly points are UTC, so they're labelled in
// UTC; hourly points use local time. Marked pure, so the embed card's bundle
// (embed.ts) leaves out the ones it doesn't use.
export const compact = /* @__PURE__ */ new Intl.NumberFormat(undefined, { notation: "compact", maximumFractionDigits: 1 });
export const dayLabel = /* @__PURE__ */ new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", timeZone: "UTC" });
const dayLabelLong = /* @__PURE__ */ new Intl.DateTimeFormat(undefined, {
  weekday: "short",
  month: "short",
  day: "numeric",
  year: "numeric",
  timeZone: "UTC"
});
const dateLabel = /* @__PURE__ */ new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" });
export const timeLabel = /* @__PURE__ */ new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit" });
export const hourLabelLong = /* @__PURE__ */ new Intl.DateTimeFormat(undefined, {
  weekday: "short",
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit"
});

// A weekly point is labelled by its Monday, so say so.
export const weekOf = (resolution: Resolution): string => (resolution === "week" ? "Week of " : "");

// A linear y axis from 0 up to `max`: its gridline step and top, about four
// steps tall.
export function linearScale(max: number): { step: number; yMax: number } {
  const step = niceStep(Math.max(max / 4, 1));
  return { step, yMax: Math.ceil(max / step) * step || step };
}

// The x axis's labels from `t0` to `t1` across `plotW` pixels: when each is,
// how it's anchored, and the format they're written in (days for daily and
// weekly points; times or dates for hourly ones, by the span). No more labels
// than buckets, or a short span labels one bucket twice.
export function timeTicks(
  t0: number,
  t1: number,
  resolution: Resolution,
  plotW: number
): { ticks: { t: number; anchor: string }[]; format: Intl.DateTimeFormat } {
  const unit = UNITS[resolution];
  const span = t1 - t0;
  const count = span === 0 ? 1 : Math.max(2, Math.min(8, Math.floor(plotW / 110), span / unit + 1));
  const ticks = [];

  for (let i = 0; i < count; i++) {
    const t = count === 1 ? t0 : t0 + Math.round((span * i) / (count - 1) / unit) * unit;
    const anchor = count === 1 ? "middle" : i === 0 ? "start" : i === count - 1 ? "end" : "middle";
    ticks.push({ t, anchor });
  }
  const format = resolution !== "hour" ? dayLabel : span <= 2 * DAY ? timeLabel : dateLabel;
  return { ticks, format };
}

// Sits `node` beside `px` (pixels across a chart `boxWidth` wide), flipping
// to the left near the right edge.
export function placeBeside(node: { offsetWidth: number; style: { left: string } }, px: number, boxWidth: number): void {
  node.style.left =
    px + 12 + node.offsetWidth > boxWidth ? `${Math.max(0, px - 12 - node.offsetWidth)}px` : `${px + 12}px`;
}

// When a hovered bucket is, as the charts' tooltips head it: an hour in local
// time, or a UTC day (a weekly one by its Monday).
export const bucketLabel = (t: number, resolution: Resolution): string =>
  resolution === "hour"
    ? hourLabelLong.format(t * 1000)
    : `${weekOf(resolution)}${dayLabelLong.format(t * 1000)} (UTC)`;

// The compare chart's scale for lines of `points`: log when the biggest
// line's highest point is more than `ratio` times the smallest's, where the
// small one would lie flat along the bottom; linear otherwise.
export function pickScale(lines: readonly (readonly Point[])[], ratio = 5): "linear" | "log" {
  const highs = lines.map((points) => points.reduce((top, p) => Math.max(top, mid(p)), 0)).filter((v) => v > 0);
  if (highs.length < 2) return "linear";
  return Math.max(...highs) > ratio * Math.min(...highs) ? "log" : "linear";
}

// A range's average (of each point's count, or daily/weekly average) and its
// high, or null for no points.
export function rangeFigures(points: readonly Point[]): { avg: number; high: number } | null {
  if (!points.length) return null;
  let sum = 0;
  let top = 0;
  for (const p of points) {
    sum += mid(p);
    top = Math.max(top, high(p));
  }
  return { avg: Math.round(sum / points.length), high: top };
}

// The embed card's sparkline (embed.ts), in its own box: stretched to fill
// the card.
export const SPARK_W = 300;
export const SPARK_H = 40;

// Hourly points as SVG paths for the sparkline: the line, and the area under
// it, each split at missing hours. Scaled from the points' low to their high,
// so the shape shows even for a game that never empties. Empty for fewer
// than two points.
export function sparkline(points: readonly Point[]): { line: string; area: string } {
  const first = points[0];
  const last = points.at(-1);
  if (!first || !last || points.length < 2) return { line: "", area: "" };

  const t0 = first.t;
  const span = last.t - t0 || 1;
  const values = points.map(mid);
  const lo = Math.min(...values);
  const hi = Math.max(...values);
  const x = (t: number): string => (((t - t0) / span) * SPARK_W).toFixed(1);
  const y = (v: number): string =>
    (hi === lo ? SPARK_H / 2 : SPARK_H - 2 - ((v - lo) / (hi - lo)) * (SPARK_H - 4)).toFixed(1);

  let line = "";
  let area = "";
  for (const segment of segmentsOf(points, HOUR)) {
    const path = segment.map((p, i) => `${i ? "L" : "M"}${x(p.t)} ${y(mid(p))}`).join("");
    line += path;
    // A lone point has no area under it.
    const start = segment[0];
    const end = segment.at(-1);
    if (start && end && start !== end) area += `${path}L${x(end.t)} ${SPARK_H}L${x(start.t)} ${SPARK_H}Z`;
  }
  return { line, area };
}
